import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import reportScheduleService from '@api/v1/reportScheduleService'
import useReadState from '@hooks/useReadState'
import { DATE_RANGE_PRESET_GROUPS, dateFilterSummary } from '@utils/cbDateSettings'
import { defaultRecurrence, WEEKDAYS } from './recurrenceDefaults'
import { fmtWhenInZone } from './queueFormat'
import SegmentedRadio from './SegmentedRadio'

/**
 * "A, B and C" — for the tied-preset sentence, which names two to four presets.
 * Not exported: a second consumer would want a locale-aware list formatter, and
 * this one is deliberately only as clever as one English sentence needs.
 */
const joinLabels = (labels) =>
  labels.length <= 1
    ? (labels[0] || '')
    : `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`

/**
 * Recurrence + roll-forward editor (spec 058, restructured for spec 120).
 *
 * Two ideas the design turns on:
 *
 * 1. **Roll-forward is the headline, not a footnote.** A schedule that pins its
 *    date range keeps re-reporting the window it was created in — and looks
 *    perfectly healthy while doing it. So the date-window control sits next to
 *    the recurrence, defaults to a rolling preset, and the preview shows the
 *    window MOVING run to run.
 * 2. **The preview is server-computed.** `nextRuns` lives in cb-api and the cron
 *    uses the same function, so what the user is shown is what will actually
 *    happen — not a second implementation that agrees today and drifts later.
 *
 * ## Why there are two variants and not two components
 *
 * This component is mounted TWICE: by `QueueReportModal` (the create modal,
 * spec 120's subject) and by `ReportSchedules.jsx`'s EDIT modal — a file this
 * stream does not own. Himaad's mock only covers the create modal, so
 * `variant="mock"` repaints the three controls it draws and `variant="legacy"`
 * (the default, and therefore the edit modal) renders exactly the DOM it always
 * has.
 *
 * The alternative — a second component under `queue/` implementing the mock's
 * Repeat section — would be a second implementation of the calendar rules, the
 * roll-forward semantics and the three-answer preview guard. That is the
 * pathology this repo keeps paying for. So the LOGIC below is single-sourced and
 * only the presentation branches:
 *
 *   legacy: freq `<select>` + interval `<input>`   mock: `Repeat` segmented control
 *   legacy: roll-forward `<select>` of 26 tokens   mock: one toggle + preset name
 *   legacy: grey "Next runs" list                  mock: blue preview strip
 *
 * Everything else — the weekly chips, the Monthly block (unmocked, preserved
 * verbatim), the time field, the preview query, the two roll-forward warnings
 * and the five-arm preview state machine — is shared and identical.
 *
 * One deliberate exception to "legacy renders exactly the DOM it always has":
 * the Weekdays preset (CC-53898, Robin E4; spec 120 amendment 2026-09-28) lives
 * in the SHARED weekly block, so the edit modal gains it too. That is the point —
 * the ticket asks for it wherever a weekly rule is edited.
 */

/*
 * Mon–Fri as the schedule wire spells it: cb-api's own weekday numbering
 * (0 = Sunday, `recurrence.js` normalizeRecurrence sorts and dedupes), and the
 * exact set its `describeRecurrence` collapses to "Every weekday …" — at
 * interval 1 only. Measured on QA 2026-09-28: stored, described and fired
 * DST-correctly by the existing engine, so this preset needs no backend change.
 */
const MON_TO_FRI = [1, 2, 3, 4, 5]

const RecurrenceEditor = ({
  value, onChange, rollForward = 'pinned', onRollForwardChange, filters, idPrefix = 'rec',
  variant = 'legacy',
  /*
   * spec 120 §7. The report's own date-preset token (`{ key, label }`) or null.
   *
   * In the `mock` variant this replaces the 26-option select with the mock's
   * single toggle, whose ON value IS this token. `null` means the report has no
   * resolvable preset — an explicit custom range, or two date filters that
   * disagree — and then the control is HIDDEN entirely and the caller pins,
   * because a toggle whose label cannot name what it will do must not be shown.
   *
   * Absent in the `legacy` variant, which keeps the full token select: the edit
   * modal is editing a STORED token that may be any of the 25, and a toggle
   * cannot express that.
   */
  rollForwardPreset,
  /*
   * spec 120 §7, second pass. The presets that ALL resolve to this report's date
   * window today — `{ value, options: [{ key, label }] }` — or null.
   *
   * Non-null only in the `mock` variant's third case, and never at the same time
   * as `rollForwardPreset`. It exists because the first pass ranked a tie by list
   * order and shipped the winner to the wire: on 2027-01-14 a Year-to-Date
   * report emitted `mtd`, and from 1 February every firing reported one month of
   * the year. The tie is real and unresolvable from the stored bytes, so it is
   * put to the user as a named list rather than decided here.
   */
  rollForwardPresetChoices,
  /*
   * Whether a schedule created from this host can store a token AT ALL — see
   * the long note on `QueueReportModal`'s prop of the same name. It never
   * changes what is emitted (a null preset already pins); it only decides WHICH
   * of the two "the dates will not move" sentences is true, because "you could
   * make it roll by setting a preset" is actionable advice on a generic report
   * and a falsehood on a specialized one.
   *
   * Spec 195: the default (edit-modal) variant honours it too — false there replaces
   * the preset select with a sentence. The Schedules page passes it per schedule kind.
   */
  rollForwardSupported = true,
  /*
   * Spec 192: replaces the "they stay where you put them" sentence when
   * `rollForwardSupported` is false and the host knows the dates DO move — the Exago
   * page's as-of can be the run's own day, resolved by its run route.
   */
  fixedDatesNote,
  /*
   * Reports the server-computed preview upward so the modal footer can say
   * "First run …" from the SAME engine the cron uses. Deriving a first run
   * locally would be a second calendar implementation; reading it off the
   * create RESPONSE is too late, because the footer renders before the create.
   * Called with null whenever there is nothing computed to report, so the
   * footer can never print a plausible run that will not happen.
   */
  onPreviewChange,
}) => {
  const rule = value || defaultRecurrence()
  const set = (patch) => onChange?.({ ...rule, ...patch })
  const isMock = variant === 'mock'

  // Debounce so dragging the interval spinner doesn't fire a request per tick.
  const [debounced, setDebounced] = useState(rule)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(rule), 300)
    return () => clearTimeout(t)
  }, [rule])

  const previewKey = useMemo(
    () => JSON.stringify({ r: debounced, f: rollForward, x: filters ?? null }),
    [debounced, rollForward, filters]
  )

  const previewQuery = useQuery({
    queryKey: ['schedule-preview', previewKey],
    queryFn: () => reportScheduleService.preview({ recurrence: debounced, rollForward, filters, count: 3 }),
    staleTime: 60_000,
  })
  /*
   * CC-53132. This read used to be `const { data: preview, isFetching }` — no
   * `error`, and `isFetching` standing in for "are we still working". Both of the
   * things this panel exists to say were then unsayable: a paused read (default
   * `networkMode: 'online'` while `onlineManager` believes the browser is
   * offline) has `isFetching === false` and no error, so `queryFn` is never
   * called and the panel fell through to "Pick a frequency to see when this will
   * run." — an instruction implying the user has configured nothing, on a panel
   * whose whole job is proof. A failed read rendered byte-identically.
   */
  const { data: preview, error: previewError, isFetching, refetch: refetchPreview } = previewQuery
  const previewState = useReadState('schedule-preview', previewQuery)

  /*
   * Hand the footer the first run and the server's own description, or nothing.
   *
   * Gated on `ready` AND a non-empty run list on purpose: every other state is a
   * state in which we do not know when this fires, and the footer's job is to
   * say what will happen, not what probably would.
   */
  useEffect(() => {
    if (!onPreviewChange) return
    const first = previewState === 'ready' ? preview?.runs?.[0] : null
    onPreviewChange(
      first ? { description: preview?.description || null, firstRunAt: first.at, tz: rule.tz } : null
    )
  }, [onPreviewChange, previewState, preview, rule.tz])

  // CC-50232 F1: the server ran the SAME predicate the write path uses and told
  // us the token cannot be stored — this report has no date-shaped filter to
  // stamp it onto. Say so. Silently showing a rolling window here while the
  // saved schedule records `pinned` is the exact failure this preview exists to
  // rule out, and the user has no reason to go and check.
  //
  // Three answers, not two, which is why this is no longer a single boolean.
  // Asking at all needs `filters` — omitted (the schedules page does not send
  // them) the server replies `rollForwardApplied: null`, i.e. "not asked", and
  // nothing is claimed either way. But when we DID ask and the read never
  // completed, `=== false` is also false, so the warning silently vanished and
  // "no warning" is exactly how a healthy report looks. Under CC-53132 that is
  // the more expensive of this file's two defects: the user saves a schedule
  // believing the window rolls, and every run re-reports the same month.
  const rollForwardAsked = rollForward !== 'pinned' && Array.isArray(filters)
  const rollForwardIgnored = rollForwardAsked && preview?.rollForwardApplied === false
  const rollForwardUnconfirmed = rollForwardAsked && (previewState === 'blocked' || previewState === 'failed')

  const monthlyMode = rule.nth ? 'nth' : 'day'

  /*
   * Weekly day chips: a 7-item multi-select with ONE tab stop and arrow keys.
   *
   * They were seven separate tab stops with no group, which is operable but not
   * what a chip row is: WAI-ARIA's pattern for a set of related toggles is a
   * roving tabindex inside a labelled group. `aria-pressed` and the full weekday
   * `aria-label` were already right and are unchanged — two chips read "T" and
   * two read "S", so a letter-only name is ambiguous to AT and to any locator.
   *
   * Applied in BOTH variants: it adds no element, changes no role and renames
   * nothing, so the edit modal gains the same keyboard behaviour with no DOM
   * text change.
   */
  const dayRefs = useRef([])
  const [dayFocus, setDayFocus] = useState(() => {
    const i = WEEKDAYS.findIndex((d) => (value?.byDay || []).includes(d.v))
    return i < 0 ? 0 : i
  })
  const onDayKeyDown = (i) => (e) => {
    const n = WEEKDAYS.length
    let next = null
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % n
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + n) % n
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = n - 1
    if (next == null) return
    e.preventDefault()
    setDayFocus(next)
    dayRefs.current[next]?.focus()
  }

  const toggleDay = (v) => {
    const cur = new Set(rule.byDay || [])
    if (cur.has(v)) cur.delete(v); else cur.add(v)
    // Never allow an empty set — a weekly rule with no day never fires. cb-api's
    // own fallback is Monday (`recurrence.js:100`), so an empty set would fire
    // silently on the wrong day rather than erroring.
    set({ byDay: cur.size ? [...cur].sort((a, b) => a - b) : [v] })
  }

  /*
   * The one-click Weekdays preset (CC-53898). What it writes, and why each part:
   *
   *   - byDay Mon–Fri, REPLACING the set rather than adding to it, so Saturday and
   *     Sunday are cleared.
   *   - interval 1. The legacy variant (the edit modal) exposes "every N weeks",
   *     and a stored 2 left in place makes this "Every 2 weeks Monday, Tuesday,
   *     Wednesday, Thursday, Friday" (measured against cb-api's
   *     describeRecurrence) — not what a control called Weekdays means. The mock
   *     variant has no interval control and already carries 1.
   *   - freq weekly, explicitly. It only renders under Weekly, so this changes
   *     nothing today; it is written so the patch states the whole rule it means.
   *
   * An ACTION, not a toggle, so it carries no `aria-pressed` and no "current"
   * styling. A pressed toggle promises that activating it again un-presses it,
   * and there is no defined "not weekdays" to return to — so the choices were a
   * toggle that silently refuses to toggle (the live-looking-control-that-
   * refuses-the-click complaint, CC-50277) or one that destroys the user's own
   * selection on the second press. A "current" look without a programmatic state
   * would fail SC 1.3.1. The state is already exposed where it lives: each chip
   * announces its own `aria-pressed`, and the server-computed description (the
   * create modal's footer, the schedules row) reads "Every weekday …". Pressing it
   * on a rule already Mon–Fri at interval 1 re-emits the identical rule, whose
   * preview key is unchanged, so it costs no request.
   *
   * DOM focus stays on the button. Only the chip group's roving tab stop moves,
   * onto Monday, so a later Tab into the group lands on a selected day rather
   * than on a Saturday the preset just cleared.
   */
  const applyWeekdays = () => {
    set({ freq: 'weekly', interval: 1, byDay: [...MON_TO_FRI] })
    setDayFocus(WEEKDAYS.findIndex((d) => d.v === MON_TO_FRI[0]))
  }

  const rollForwardOn = rollForward !== 'pinned'
  const previewRuns = previewState === 'ready' ? (preview?.runs || []) : []

  /*
   * The two CC-50232 warnings, hoisted so all THREE roll-forward branches carry
   * them. They used to be typed out inside the two branches that existed, and a
   * third branch that quietly did not render them would look exactly like a
   * healthy report — which is the failure they exist to prevent.
   */
  const rollForwardWarnings = (
    <>
      {rollForwardIgnored && (
        <p role="status" className="mt-1.5 text-[11.5px] font-medium text-cb-navy">
          This report has no date filter to roll forward, so every run will use
          the filters exactly as they are now. Set a date range on the report
          first if you want the window to move.
        </p>
      )}
      {rollForwardUnconfirmed && (
        <p role="status" className="mt-1.5 text-[11.5px] font-medium text-cb-navy">
          We couldn&apos;t check whether this report has a date filter for the window to
          roll forward on, so nothing here says that it does. Try again below before
          you save.
        </p>
      )}
    </>
  )

  return (
    <div className="grid gap-3.5">
      {/* Frequency */}
      {isMock ? (
        <div>
          <span id={`${idPrefix}-freq-label`} className="mb-1.5 block text-[11px] font-semibold text-rc-ink">
            Repeat
          </span>
          <SegmentedRadio
            name={`${idPrefix}-freq-seg`}
            labelledBy={`${idPrefix}-freq-label`}
            value={rule.freq}
            onChange={(v) => set({ freq: v })}
            options={[
              { key: 'daily', label: 'Daily' },
              { key: 'weekly', label: 'Weekly' },
              { key: 'monthly', label: 'Monthly' },
            ]}
          />
        </div>
      ) : (
        <div>
          <label htmlFor={`${idPrefix}-freq`} className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            Repeats
          </label>
          {/* 2026-08-26 ADA sweep. Every control in this editor carried its own
              `border-gray-200 rounded-lg px-2.5 py-1.5 text-[12.5px]`; #e5e7eb is
              1.24:1 and on these white-on-white controls the 1px border is the
              only thing identifying them, so SC 1.4.11 owes it 3.0:1
              (--color-rc-control, 3.03:1). `.select-rc` / `.input-rc` carry the
              border, the 6px radius and the shared focus outline. The geometry
              utilities are DELETED rather than paired — both classes are unlayered
              and would have made every one of them dead code. */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              id={`${idPrefix}-freq`}
              value={rule.freq}
              onChange={(e) => set({ freq: e.target.value })}
              className="select-rc"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <span className="text-[12.5px] text-gray-500">every</span>
            <label htmlFor={`${idPrefix}-interval`} className="sr-only">Repeat interval</label>
            <input
              id={`${idPrefix}-interval`}
              type="number"
              min={1}
              max={52}
              value={rule.interval}
              onChange={(e) => set({ interval: Math.max(1, Math.min(52, Number(e.target.value) || 1)) })}
              className="w-16 input-rc"
            />
            <span className="text-[12.5px] text-gray-500">
              {rule.freq === 'daily' ? 'day(s)' : rule.freq === 'weekly' ? 'week(s)' : 'month(s)'}
            </span>
          </div>
        </div>
      )}

      {/* Weekly — which days */}
      {rule.freq === 'weekly' && (
        <div>
          <span id={`${idPrefix}-days-label`} className={isMock
            ? 'mb-1.5 block text-[11px] font-semibold text-rc-ink'
            : 'mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500'}
          >
            On these days
          </span>
          {/* The preset sits BESIDE the chip group, never inside it: the group is a
              seven-chip roving-tabindex set (one tab stop, arrows move within it),
              and an eighth member would be a "chip" the arrow keys land on that
              selects five days. `flex-wrap` so a narrow modal drops it under the
              chips rather than overflowing. */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
            <div role="group" aria-labelledby={`${idPrefix}-days-label`} className="flex gap-1.5">
              {WEEKDAYS.map((d, i) => {
                const on = (rule.byDay || []).includes(d.v)
                return (
                  <button
                    key={d.v}
                    type="button"
                    ref={(el) => { dayRefs.current[i] = el }}
                    // aria-pressed (not just colour) so the selection is announced.
                    aria-pressed={on}
                    aria-label={d.full}
                    tabIndex={i === dayFocus ? 0 : -1}
                    onFocus={() => setDayFocus(i)}
                    onKeyDown={onDayKeyDown(i)}
                    onClick={() => toggleDay(d.v)}
                    // Unpressed, the border is the only thing drawing this as a
                    // control: --color-rc-control (3.03:1), not #e5e7eb (1.24:1).
                    // Pressed is a solid fill, which needs no outline of its own —
                    // mock blue #1d4ed8 carries white at 6.70:1, legacy navy at
                    // 16.3:1. Both clear 4.5:1 as text.
                    className={`h-8 w-8 rounded-full text-[12px] font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-cb-navy ${
                      on
                        ? (isMock ? 'bg-rc-chip-text text-white' : 'bg-cb-navy text-white')
                        : 'border border-rc-control text-gray-600 hover:border-cb-accent'
                    }`}
                  >
                    {d.label}
                  </button>
                )
              })}
            </div>
            {/* A native <button>, so Enter and Space operate it with nothing of
                ours to get wrong — asserted from the keyboard in both hosts.
                Visible name "Weekdays"; the accessible name STARTS with it (SC
                2.5.3) and spells the rule out, and is never exactly "Weekday" —
                that is the Monthly block's select, located by that exact label in
                two suites. Both modal bodies it renders on are #ffffff, so:
                  text   --color-cb-accent-text #064891   8.96:1  (needs 4.5)
                  border --color-rc-control     #8b95a5   3.03:1  (needs 3.0)
                  hover  --color-rc-seam        #6b7280   4.83:1  (needs 3.0)
                  focus  --color-cb-navy        #09203d  16.3:1   (needs 3.0)
                Hover darkens the BORDER rather than tinting the fill: rc-control
                is 2.90:1 on even gray-50 #f9fafb. No `.btn-rc` here — it
                is unlayered and would silently defeat the h-8 that keeps this level
                with the 32px chips. */}
            <button
              type="button"
              onClick={applyWeekdays}
              aria-label="Weekdays, every Monday to Friday"
              className="h-8 rounded-full border border-rc-control px-3 text-[12px] font-semibold text-cb-accent-text transition-colors hover:border-rc-seam focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-cb-navy"
            >
              Weekdays
            </button>
          </div>
        </div>
      )}

      {/* Monthly — calendar day or nth weekday.
          UNMOCKED: neither screenshot shows Monthly expanded, so this block is
          preserved verbatim in both variants rather than invented. */}
      {rule.freq === 'monthly' && (
        <div className="grid gap-2">
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">On</span>
          <label className="flex items-center gap-2 text-[12.5px] text-gray-700">
            <input
              type="radio"
              name={`${idPrefix}-monthly`}
              checked={monthlyMode === 'day'}
              onChange={() => set({ nth: undefined, byMonthDay: rule.byMonthDay || 1 })}
            />
            <span>day</span>
            <label htmlFor={`${idPrefix}-dom`} className="sr-only">Day of month</label>
            {/* These three monthly selects are genuinely toggled disabled by the
                radio above them, so the disabled LOOK is load-bearing — a live-
                looking control that refuses the click is the CC-50277 complaint.
                `disabled:bg-gray-50` and `disabled:text-gray-400` cannot do that
                job any more: `.select-rc` is unlayered, so its `background-color`
                and `color` beat both, and leaving them would have been dead code
                that reads like a disabled state and isn't one. `opacity` is not
                set by `.select-rc`, so `disabled:opacity-50` DOES apply — and it
                is the same cue `.btn-rc:disabled` / `.input-rc:disabled` use. */}
            <select
              id={`${idPrefix}-dom`}
              value={rule.byMonthDay ?? 1}
              disabled={monthlyMode !== 'day'}
              onChange={(e) => set({ byMonthDay: Number(e.target.value), nth: undefined })}
              className="select-rc disabled:opacity-50"
            >
              {Array.from({ length: 31 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
              <option value={-1}>last day</option>
            </select>
          </label>
          <label className="flex flex-wrap items-center gap-2 text-[12.5px] text-gray-700">
            <input
              type="radio"
              name={`${idPrefix}-monthly`}
              checked={monthlyMode === 'nth'}
              onChange={() => set({ nth: { n: 1, weekday: 1 }, byMonthDay: undefined })}
            />
            <span>the</span>
            <label htmlFor={`${idPrefix}-nth`} className="sr-only">Which occurrence</label>
            <select
              id={`${idPrefix}-nth`}
              value={rule.nth?.n ?? 1}
              disabled={monthlyMode !== 'nth'}
              onChange={(e) => set({ nth: { ...(rule.nth || { weekday: 1 }), n: Number(e.target.value) } })}
              className="select-rc disabled:opacity-50"
            >
              <option value={1}>1st</option><option value={2}>2nd</option><option value={3}>3rd</option>
              <option value={4}>4th</option><option value={-1}>last</option>
            </select>
            <label htmlFor={`${idPrefix}-nthday`} className="sr-only">Weekday</label>
            <select
              id={`${idPrefix}-nthday`}
              value={rule.nth?.weekday ?? 1}
              disabled={monthlyMode !== 'nth'}
              onChange={(e) => set({ nth: { ...(rule.nth || { n: 1 }), weekday: Number(e.target.value) } })}
              className="select-rc disabled:opacity-50"
            >
              {WEEKDAYS.map((d) => <option key={d.v} value={d.v}>{d.full}</option>)}
            </select>
          </label>
        </div>
      )}

      {/* Time of day.
          The mock shows a bare "7:00 AM" with no zone. The zone LABEL is kept
          anyway, and this is a deliberate deviation recorded in spec 120: a
          schedule fires at ITS OWN wall time, the preview below is formatted in
          that zone precisely so it cannot contradict this field, and dropping
          the label leaves a financial schedule's firing time ambiguous by five
          to six hours with nothing on screen to resolve it. */}
      <div>
        <label htmlFor={`${idPrefix}-time`} className={isMock
          ? 'mb-1.5 block text-[11px] font-semibold text-rc-ink'
          : 'mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500'}
        >
          At
        </label>
        <div className="flex items-center gap-2">
          <input
            id={`${idPrefix}-time`}
            type="time"
            value={`${String(rule.hour).padStart(2, '0')}:${String(rule.minute).padStart(2, '0')}`}
            onChange={(e) => {
              const [h, m] = String(e.target.value || '07:00').split(':')
              set({ hour: Number(h) || 0, minute: Number(m) || 0 })
            }}
            className="input-rc"
          />
          <span className="text-[11.5px] text-gray-500">{rule.tz}</span>
        </div>
      </div>

      {/* Roll-forward — the control that decides whether this schedule stays useful */}
      {isMock ? (
        /*
         * spec 120 §7. Rolling stores THIS REPORT's preset token; fixed stores
         * 'pinned'. No module default ever: the whole defect being fixed is a
         * hardcoded 'lastMonth' that made a This-Month report schedule LAST
         * month while the preview agreed with itself.
         *
         * THREE branches, not two, and the middle one is the correction to the
         * first pass of this fix. `rollForwardPreset` is non-null only when
         * exactly ONE preset resolves to the report's window; when several do,
         * `rollForwardPresetChoices` carries them and the user names one,
         * because ranking a tie by list order is what shipped `mtd` for a
         * Year-to-Date report (see `rollForwardPreset.js` refusal 3). The two
         * props are never non-null together, by construction.
         *
         * No preset at all ⇒ no CONTROL. But not silence: a recurring schedule
         * whose window never moves is the failure mode this whole panel exists
         * to make visible, and it looks identical to a healthy one. So the fact
         * is still stated, in whichever of the two forms is true here.
         */
        rollForwardPreset ? (
          /*
           * The mock draws a sliding toggle here and this is a `SegmentedRadio`
           * instead — a declared deviation (spec 120 D16), on measured contrast
           * rather than taste.
           *
           * `ToggleSwitch` was used for one commit and every graphic it owns is
           * under the SC 1.4.11 3.0:1 floor, recomputed against the white modal
           * body it actually sits on (`ToggleSwitch.jsx:45-53`):
           *   OFF track #d1d5db on #ffffff              1.47:1
           *   ON track  --color-cb-green #34c475        2.26:1
           *   ON vs OFF track                           1.53:1
           *   white knob on either track          1.47 / 2.26:1
           * The pill's boundary IS the only thing identifying it as a control, so
           * at 1.47:1 the control is not identifiable, and its own docstring
           * nominates knob POSITION as the non-colour state cue — a cue at
           * 1.47:1 against the thing it moves across. Two states here send
           * different wire tokens (`thisMonth` vs `pinned`), i.e. two different
           * sets of billable-hours figures on a cadence, decided by a control a
           * low-vision user cannot read. `origin/qa` indicated this state as
           * option TEXT in a select and the edit modal (the legacy branch below)
           * still does, so the toggle was a regression, not inherited debt.
           *
           * `SegmentedRadio` is already this modal's shape for a small closed
           * choice, is a real radio group (single tab stop, native arrow keys,
           * `checked` exposed), and every one of its pixels measures over the
           * floor — see its own file for the recomputed table. Fixing
           * `ToggleSwitch` itself would repoint `--color-cb-green` for consumers
           * this stream does not own; that is filed, not done here (spec 120 F9).
           */
          <div>
            <span id={`${idPrefix}-roll-label`} className="mb-1.5 block text-[11px] font-semibold text-rc-ink">
              Date range each run
            </span>
            <SegmentedRadio
              name={`${idPrefix}-roll`}
              labelledBy={`${idPrefix}-roll-label`}
              value={rollForwardOn ? 'roll' : 'pinned'}
              onChange={(k) => onRollForwardChange?.(k === 'roll' ? rollForwardPreset.key : 'pinned')}
              options={[
                {
                  key: 'roll',
                  label: 'Rolls forward',
                  ariaLabel: `Roll the date range forward to ${rollForwardPreset.label} on every run`,
                },
                {
                  key: 'pinned',
                  label: 'Stays fixed',
                  ariaLabel: 'Keep the dates exactly as they are now on every run',
                },
              ]}
            />
            <p className="mt-1.5 text-[11.5px] text-rc-muted">
              {rollForwardOn ? (
                <>Each run reports on <b>{rollForwardPreset.label}</b> as of the day it fires.</>
              ) : (
                <>Every run keeps the dates on this report right now, exactly as they are.</>
              )}
            </p>
            {rollForwardWarnings}
          </div>
        ) : rollForwardPresetChoices ? (
          /*
           * The tie. Several presets cover this report's window today and
           * diverge later, and nothing stored says which one the user set — so
           * this asks, and defaults to the fixed window because a default here
           * would be the guess the whole refusal exists to avoid.
           *
           * A `<select>` rather than segments: there are two to four candidates
           * with names as long as "Last Year To Present", which segments cannot
           * carry. `.select-rc` is unlayered and owns its border, radius and
           * focus outline (--color-rc-control #8b95a5, 3.03:1 on the white body;
           * `src/index.css`), so NO `h-`/`text-`/`px-`/`border-`/`rounded-`
           * utility is paired with it here — those would be dead code. The state
           * is the selected option's TEXT, which is the same way `origin/qa`
           * indicated it and is over 10:1.
           */
          <div>
            <label htmlFor={`${idPrefix}-roll-pick`} className="mb-1.5 block text-[11px] font-semibold text-rc-ink">
              Date range each run
            </label>
            <select
              id={`${idPrefix}-roll-pick`}
              value={rollForward}
              onChange={(e) => onRollForwardChange?.(e.target.value)}
              className="w-full select-rc"
            >
              <option value="pinned">Keep the dates fixed — they will not move</option>
              {rollForwardPresetChoices.options.map((o) => (
                <option key={o.key} value={o.key}>{o.label} — rolls forward</option>
              ))}
            </select>
            <p className="mt-1.5 text-[11.5px] text-rc-muted">
              {/* A tie can never be All Dates: `$$cb_all` is produced by exactly
                  one preset and a proc report's bare `|` by none, so neither can
                  reach this branch and `dateFilterSummary` needs no sentinel
                  guard here. */}
              {joinLabels(rollForwardPresetChoices.options.map((o) => o.label))} all cover{' '}
              <b>{dateFilterSummary(rollForwardPresetChoices.value)}</b> right now, so this
              report&apos;s dates cannot say which one each run should use. Name one, or leave
              the dates where they are.
            </p>
            {rollForwardWarnings}
          </div>
        ) : (
          <p className="text-[11.5px] text-rc-muted">
            {rollForwardSupported ? (
              <>
                Every run keeps the dates on this report exactly as they are now. Set one of
                the report&apos;s own date presets — This Month, Year to Date — if you want the
                window to move with each run.
              </>
            ) : fixedDatesNote ? (
              <span data-testid="schedule-dates-note">{fixedDatesNote}</span>
            ) : (
              <>
                Every run repeats the date range shown above. This report&apos;s dates are its
                own fields rather than a rolling window, so they stay where you put them.
              </>
            )}
          </p>
        )
      ) : !rollForwardSupported ? (
        /*
         * Spec 195 — the edit modal of a schedule whose runs cannot roll forward (every
         * kind but generic: `buildScheduleJob` re-resolves a preset only on the generic
         * path, and a replay's stored request is not a filter array the token can ride).
         * This branch used to offer the preset select anyway, so a user could choose
         * "Last Month — rolls forward" on a specialized schedule, save it, and keep
         * getting the same window. No control, but not silence: the fact is stated. It
         * claims neither "fixed" nor "rolling", because an Exago schedule saved with As Of
         * today (spec 192) rolls by its own request and this modal cannot see which.
         */
        <div data-testid={`${idPrefix}-dates-saved`}>
          <span className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            Date range each run
          </span>
          <p className="text-[11.5px] text-gray-500">
            Every run uses the dates saved with this schedule. They are part of this report&apos;s own
            request rather than a rolling preset, so they can&apos;t be changed here — open the report
            and schedule it again to use different dates.
          </p>
        </div>
      ) : (
        <div>
          <label htmlFor={`${idPrefix}-roll`} className="mb-1.5 block text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            Date range each run
          </label>
          <select
            id={`${idPrefix}-roll`}
            value={rollForward}
            onChange={(e) => onRollForwardChange?.(e.target.value)}
            className="w-full select-rc"
          >
            <option value="pinned">Keep the dates I picked (fixed window)</option>
            {DATE_RANGE_PRESET_GROUPS.map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.presets.map((p) => <option key={p.key} value={p.key}>{p.label} — rolls forward</option>)}
              </optgroup>
            ))}
          </select>
          <p className="mt-1.5 text-[11.5px] text-gray-500">
            {rollForward === 'pinned'
              ? 'Every run asks for the exact dates on this report right now. Fine for a one-off; a recurring report usually wants a rolling window.'
              : 'Resolved fresh on every run, so the report keeps meaning the same thing as time moves.'}
          </p>
          {/* Same fragment the mock branches render, so the edit modal's DOM for
              these two warnings is byte-identical to what it has always been. */}
          {rollForwardWarnings}
        </div>
      )}

      {/* Preview — proof, not a promise.
          The mock draws a blue strip listing the resolved windows joined by
          ' · '. The four NON-ready arms are unchanged in both variants: they are
          the CC-53132 guard, and "hide it if it cannot be computed" must not
          collapse "we could not ask" into "nothing to show". */}
      <div className={isMock ? '' : 'rounded-xl border border-gray-100 bg-gray-50 px-3 py-2.5'}>
        {!isMock && (
          <div className="mb-1.5 text-[10.5px] font-semibold uppercase tracking-wider text-gray-500">
            {/* The hint belongs to a REFETCH over a preview already on screen. On the
                first fetch the body says so itself, and showing both read as two
                different things happening. */}
            Next runs {previewState === 'ready' && isFetching && (
              <span className="font-normal normal-case tracking-normal text-gray-400">· checking…</span>
            )}
          </div>
        )}
        {/* `idle` rides with loading. It is unreachable today — this query has no
            `enabled` option — but every branch here is gated on a named state, so
            an unhandled one would leave the panel with a header and no body, and
            whoever added the gate would get that as their reward. */}
        {previewState === 'loading' || previewState === 'idle' ? (
          <p className="text-[12.3px] text-gray-500">Working out the next runs…</p>
        ) : previewState === 'blocked' ? (
          /* The state that used to render as "Pick a frequency". react-query
             PAUSES a query while it believes the browser is offline: no request,
             no error, no spinner — and a preview that quietly shows nothing is
             indistinguishable from a rule that never fires. */
          <div role="alert" className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[12.3px] leading-relaxed text-amber-900">
            <p className="font-medium">Can&apos;t reach the server, so we haven&apos;t worked out when this will run.</p>
            <p className="mt-1 text-amber-800">
              This is not a schedule that never runs — your connection looks offline.
              Check it and try again.
            </p>
            <button
              type="button"
              onClick={() => refetchPreview()}
              className="mt-2 rounded-lg border border-amber-300 bg-white px-3 py-1.5 text-[11.5px] font-medium text-amber-900 hover:bg-amber-100"
            >
              Try again
            </button>
          </div>
        ) : previewState === 'failed' ? (
          <div role="alert" className="rounded-lg border border-red-100 bg-red-50 px-2.5 py-2 text-[12.3px] leading-relaxed text-red-700">
            <p className="font-medium">We couldn&apos;t work out when this will run: {previewError?.message}</p>
            <p className="mt-1">This is not a schedule that never runs.</p>
            <button
              type="button"
              onClick={() => refetchPreview()}
              className="mt-2 rounded-lg border border-red-200 bg-white px-3 py-1.5 text-[11.5px] font-medium text-red-700 hover:bg-red-100"
            >
              Try again
            </button>
          </div>
        ) : previewRuns.length ? (
          isMock ? (
            /*
             * The mock's blue strip. Measured against the actual backgrounds,
             * with the token file's own numbers (`src/index.css:123-125`):
             *   text   --color-rc-chip-text #1d4ed8 on --color-rc-chip-bg #eff6ff  6.2:1
             *   border --color-rc-chip-line #7391e7  3.03:1 on white / 2.78:1 on
             *          the strip's own #eff6ff fill
             * The border is under 3.0:1 on its inner side, and that is in bounds
             * here but would not be everywhere: this strip is a static panel, so
             * its outline identifies no control and indicates no state — the
             * decorative case `src/index.css:90` exempts. Do NOT copy this pair
             * onto anything interactive: that position wants --color-rc-seam
             * #6b7280, which is 4.44:1 on this same #eff6ff fill and exists
             * precisely because neither rc-control (2.780) nor rc-chip-line
             * (2.783) clears the floor there.
             * Each window keeps its OWN <span> rather than being flattened into
             * one sentence, so `getByText('08/01/2026 to 08/31/2026')` still
             * addresses one element — a joined string would make every window
             * assertion in this subsystem a substring match.
             */
            <p className="rounded-rc-card border border-rc-chip-line bg-rc-chip-bg px-3 py-2 text-[11.7px] leading-relaxed text-rc-chip-text">
              {previewRuns.map((r, i) => (
                <span key={r.at}>
                  {/* NOT aria-hidden. It is the only thing between two runs —
                      JSX strips the newline-bearing whitespace around this
                      expression — so hiding it made the accessible text read
                      "…09/30/2026Monday 21 September…" as one token, on the one
                      panel whose job is proving WHICH window each firing covers.
                      Visually unchanged; the dot was always in `textContent`, so
                      no assertion moves either. */}
                  {i > 0 && <span> · </span>}
                  {/* In the SCHEDULE's zone, not the viewer's — otherwise the
                      preview contradicts the "At 07:00" control right above it. */}
                  <span>{fmtWhenInZone(r.at, rule.tz)}</span>
                  {r.window && (
                    <>
                      {' covers '}
                      <span className="tabular-nums">
                        {/* All Dates has two wire spellings — `$$cb_all` on BASIC
                            reports, a bare `|` on proc-based ones. This guard comes
                            FIRST and stays exact: a bare `|` handed to ANY range
                            formatter prints a separator with nothing either side. */}
                        {r.window === '$$cb_all' || r.window === '|'
                          ? 'all dates'
                          : dateFilterSummary(r.window)}
                      </span>
                    </>
                  )}
                </span>
              ))}
              {isFetching && <span className="font-normal opacity-70"> · checking…</span>}
            </p>
          ) : (
            <ul className="grid gap-1">
              {previewRuns.map((r) => (
                <li key={r.at} className="flex flex-wrap items-baseline justify-between gap-2 text-[12.3px] text-gray-700">
                  {/* In the SCHEDULE's zone, not the viewer's — otherwise the
                      preview contradicts the "At 07:00" control right above it. */}
                  <span>{fmtWhenInZone(r.at, rule.tz)}</span>
                  {r.window && (
                    <span className="tabular-nums text-[11.5px] text-gray-500">
                      {/* All Dates has two wire spellings — `$$cb_all` on BASIC
                          reports, a bare `|` on proc-based ones (CC-50232 F1;
                          cb-api `recurrence.js:336` is the other half of the
                          pair). This guard comes FIRST and stays exact: a bare
                          `|` handed to ANY range formatter prints a separator
                          with nothing either side.
                          Everything else is a resolved wire range, formatted for
                          READING only — `dateFilterSummary` renders the halves
                          MM/DD/YYYY (Himaad UI feedback, 2026-08-27) instead of
                          showing the user raw ISO, and it is the same producer
                          behind every filter pill on every report, so this
                          preview and the report's own pills cannot drift apart.
                          Display only: `r.window` is the SERVER-computed
                          preview's own value and nothing here goes back to CB. */}
                      {r.window === '$$cb_all' || r.window === '|'
                        ? 'all dates'
                        : dateFilterSummary(r.window)}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )
        ) : (
          /* Only reachable now on a COMPLETED read that came back with no runs —
             the one case where "we have nothing to show you" is a fact. */
          <p className="text-[12.3px] text-gray-500">Pick a frequency to see when this will run.</p>
        )}
      </div>
    </div>
  )
}

export default RecurrenceEditor
